import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { msalInstance } from '../auth/msalInstance';

/**
 * API Client class for making HTTP requests to the backend
 * Automatically adds authentication tokens to requests
 */
class ApiClient {
  private axiosInstance: AxiosInstance;
  private tokenProvider: (() => Promise<string | null>) | null = null;
  private impersonatedUser: string | null = null;
  private isHandlingUnauthorized = false;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Set the token provider function (called before each request)
   */
  setTokenProvider(provider: () => Promise<string | null>) {
    this.tokenProvider = provider;
  }

  /**
   * Set the impersonated user (developer-only feature)
   */
  setImpersonatedUser(username: string | null) {
    this.impersonatedUser = username;
  }

  /**
   * Get the current impersonated user
   */
  getImpersonatedUser(): string | null {
    return this.impersonatedUser;
  }

  /**
   * Get the current authentication token
   */
  async getToken(): Promise<string | null> {
    if (this.tokenProvider) {
      try {
        return await this.tokenProvider();
      } catch (error) {
        console.error('Failed to acquire token:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Clear MSAL session and redirect to login once per unauthorized burst.
   */
  private async handleUnauthorized(): Promise<void> {
    if (this.isHandlingUnauthorized) {
      return;
    }
    this.isHandlingUnauthorized = true;

    console.error('Unauthorized request - token may be expired or invalid');
    sessionStorage.setItem('auth:token_expired', 'true');

    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;

    try {
      await msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      });
    } catch (logoutError) {
      console.error('Failed to clear MSAL session on 401:', logoutError);
      msalInstance.setActiveAccount(null);
      window.location.href = '/login';
    }
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors() {
    // Request interceptor - add auth token and impersonation header
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        if (this.tokenProvider) {
          try {
            const token = await this.tokenProvider();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.error('Failed to acquire token:', error);
          }
        }
        
        // Add impersonation header if set (developer-only feature)
        if (this.impersonatedUser) {
          config.headers['X-Impersonate-User'] = this.impersonatedUser;
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle common errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response) {
          // Handle 401 Unauthorized - token expired or invalid
          if (error.response.status === 401) {
            void this.handleUnauthorized();
            return Promise.reject(error);
          }

          // Handle 403 Forbidden
          if (error.response.status === 403) {
            console.error('Forbidden - insufficient permissions');
          }

          // Handle 404 Not Found
          if (error.response.status === 404) {
            console.error('Resource not found:', error.config?.url);
          }

          // Handle 500 Server Error
          if (error.response.status >= 500) {
            console.error('Server error:', error.response.status);
          }
        } else if (error.request) {
          console.error('No response received:', error.message);
        } else {
          console.error('Request error:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  /**
   * Get the underlying axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Create and export singleton instance
const apiClient = new ApiClient(import.meta.env.VITE_API_BASE_URL || '');

export default apiClient;
export { ApiClient };
